import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import DataGrid from './DataGrid';
import DetailsDialog from './DetailsDialog';
import ManageProjectsModal from './ManageProjectsModal';
import LinkCoderDialog from './LinkCoderDialog';
import AILinkCoderDialog from './AILinkCoderDialog';
import useFirestore from '../hooks/useFirestore';
import useProjects from '../hooks/useProjects';
import useFieldConfig from '../hooks/useFieldConfig';
import { useFirebase } from '../context/FirebaseContext';
import { getTranslations } from '../utils/translations';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import './MainPage.css';

const MainPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useFirebase();
  const { loadEntries, insertEntry, updateEntry, deleteEntry, deleteMultipleEntries } = useFirestore();
  const { projects, loadProjects, saveProject, deleteProject } = useProjects();
  const { loadFieldConfig, saveFieldConfig, getDefaultFieldConfig } = useFieldConfig();

  const [selectedProject, setSelectedProject] = useState(() => {
    return localStorage.getItem('linkcoder-selected-project') || '';
  });
  const [entries, setEntries] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [fieldConfig, setFieldConfig] = useState(null);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('linkcoder-language') || 'nl';
  });

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [manageProjectsModalOpen, setManageProjectsModalOpen] = useState(false);
  const [linkCoderDialogOpen, setLinkCoderDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  
  const [currentItem, setCurrentItem] = useState(null);
  const [dialogMode, setDialogMode] = useState('Add');

  // Load projects on mount, but only if user is logged in
  useEffect(() => {
    if (currentUser) {
      loadProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Auto-select project when projects are loaded
  useEffect(() => {
    if (projects.length > 0) {
      const savedProject = localStorage.getItem('linkcoder-selected-project');
      
      // Check if saved project exists in the list
      if (savedProject && projects.includes(savedProject)) {
        setSelectedProject(savedProject);
      } else if (!selectedProject) {
        // No saved project or it doesn't exist anymore, select first
        setSelectedProject(projects[0]);
        localStorage.setItem('linkcoder-selected-project', projects[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // Load entries and field config when project changes, but only if user is logged in
  useEffect(() => {
    if (currentUser && selectedProject) {
      loadEntriesForProject();
      loadFieldConfigForProject();
    } else {
      setEntries([]);
      setFieldConfig(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, currentUser]);

  // Ensure latest field config is used when returning focus to this page
  useEffect(() => {
    const handleFocus = () => {
      if (currentUser && selectedProject) {
        loadFieldConfigForProject();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedProject]);

  // Keyboard shortcut: Ctrl+N to open add form
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Ctrl+N (or Cmd+N on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        event.preventDefault(); // Prevent browser's default "new window" action
        
        // Only open if user is logged in and project is selected
        if (currentUser && selectedProject) {
          handleAddLink();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedProject]); // handleAddLink only depends on selectedProject which is already in deps

  const loadEntriesForProject = async () => {
    setLoading(true);
    try {
      const data = await loadEntries(selectedProject);
      setEntries(data);
    } catch (error) {
      const errorMsg = language === 'nl' 
        ? 'Fout bij laden van data: ' + error.message
        : 'Error loading data: ' + error.message;
      showMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loadFieldConfigForProject = async () => {
    try {
      const config = await loadFieldConfig(selectedProject);
      setFieldConfig(config);
    } catch (error) {
      console.error('Error loading field config:', error);
      setFieldConfig(getDefaultFieldConfig());
    }
  };

  const showMessage = (msg) => {
    setMessage(msg);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('linkcoder-language', newLanguage);
  };

  // Project handlers
  const handleProjectChange = (event) => {
    const value = event.target.value;
    
    // Check if the special "manage projects" option was selected
    if (value === '__manage_projects__') {
      setManageProjectsModalOpen(true);
      return;
    }
    
    setSelectedProject(value);
    localStorage.setItem('linkcoder-selected-project', value);
    setSelectedItems([]);
  };

  // Entry handlers
  const handleAddLink = () => {
    if (!selectedProject) {
      return;
    }
    // Refresh config to apply latest normalization toggles
    loadFieldConfigForProject();
    setCurrentItem(null);
    setDialogMode('Add');
    setDetailsDialogOpen(true);
  };

  const handleRowDoubleClick = (event) => {
    setCurrentItem(event.data);
    setDialogMode('Edit');
    setDetailsDialogOpen(true);
  };

  const handleEditEntry = (entry) => {
    // Refresh config to apply latest normalization toggles
    loadFieldConfigForProject();
    setCurrentItem(entry);
    setDialogMode('Edit');
    setDetailsDialogOpen(true);
  };

  const handleCloneEntry = async (entry) => {
    // Create a copy without Id and _firestoreId
    const clonedEntry = { ...entry };
    delete clonedEntry.Id;
    delete clonedEntry._firestoreId;
    
    // Generate temporary ID for optimistic update
    const tempId = 'temp_' + Date.now();
    const optimisticEntry = { ...clonedEntry, Id: tempId };
    
    // Add to UI immediately (optimistic update) - prepend to top when no sort active
    setEntries(prevEntries => [optimisticEntry, ...prevEntries]);
    showMessage(language === 'nl' ? 'Regel gekopieerd' : 'Row copied');
    
    // Save to database in background
    try {
      const newEntry = await insertEntry({ ...clonedEntry, Project: selectedProject });
      // Replace temp entry with real entry
      setEntries(prevEntries => 
        prevEntries.map(e => e.Id === tempId ? newEntry : e)
      );
    } catch (error) {
      console.error('Fout bij kopiëren:', error);
      showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
      // Remove temp entry on error
      setEntries(prevEntries => prevEntries.filter(e => e.Id !== tempId));
    }
  };

  const handleCellValueChanged = async (event) => {
    const updatedEntry = event.data;
    const originalEntry = entries.find(e => e.Id === updatedEntry.Id);
    
    // Update UI immediately (optimistic update)
    setEntries(prevEntries => 
      prevEntries.map(entry => 
        entry.Id === updatedEntry.Id ? updatedEntry : entry
      )
    );
    showMessage(language === 'nl' ? 'Veld bijgewerkt' : 'Field updated');
    
    // Update database in background
    try {
      await updateEntry(updatedEntry);
    } catch (error) {
      console.error('Error updating cell:', error);
      showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
      // Revert to original on error
      if (originalEntry) {
        setEntries(prevEntries => 
          prevEntries.map(entry => 
            entry.Id === updatedEntry.Id ? originalEntry : entry
          )
        );
      }
    }
  };

  const handleSaveEntry = async (data) => {
    if (dialogMode === 'Add') {
      // Generate temporary ID for optimistic update
      const tempId = 'temp_' + Date.now();
      const optimisticEntry = { ...data, Id: tempId };
      
      // Add to UI immediately (optimistic update) - prepend to top when no sort active
      setEntries(prevEntries => [optimisticEntry, ...prevEntries]);
      setDetailsDialogOpen(false);
      showMessage(language === 'nl' ? 'Regel toegevoegd' : 'Row added');
      
      // Save to database in background
      try {
        const newEntry = await insertEntry({ ...data, Project: selectedProject });
        // Replace temp entry with real entry
        setEntries(prevEntries => 
          prevEntries.map(e => e.Id === tempId ? newEntry : e)
        );
      } catch (error) {
        console.error('Fout bij opslaan:', error);
        showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
        // Remove temp entry on error
        setEntries(prevEntries => prevEntries.filter(e => e.Id !== tempId));
      }
      
    } else if (dialogMode === 'Edit') {
      const updatedEntry = { ...currentItem, ...data };
      
      // Update UI immediately (optimistic update)
      setEntries(prevEntries => 
        prevEntries.map(entry => 
          entry.Id === currentItem.Id ? updatedEntry : entry
        )
      );
      setDetailsDialogOpen(false);
      showMessage(language === 'nl' ? 'Regel aangepast' : 'Row updated');
      
      // Save to database in background
      try {
        await updateEntry(updatedEntry);
      } catch (error) {
        console.error('Fout bij opslaan:', error);
        showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
        // Revert on error
        setEntries(prevEntries => 
          prevEntries.map(entry => 
            entry.Id === currentItem.Id ? currentItem : entry
          )
        );
      }
      
    } else if (dialogMode === 'BatchEdit') {
      // Optimistic update for batch edit
      const itemsToUpdate = [...selectedItems];
      const updatedItems = itemsToUpdate.map(item => {
        const updatedItem = { ...item };
        // Only update fields that are not the "different values" placeholder
        Object.keys(data).forEach((key) => {
          if (data[key] && data[key] !== '' && data[key] !== '-- verschilt per regel --') {
            updatedItem[key] = data[key];
          }
        });
        return updatedItem;
      });

      // Update UI immediately
      setEntries(prevEntries => 
        prevEntries.map(entry => {
          const updated = updatedItems.find(u => u.Id === entry.Id);
          return updated || entry;
        })
      );
      setDetailsDialogOpen(false);
      const msg = language === 'nl' 
        ? `${itemsToUpdate.length} regel(s) aangepast`
        : `${itemsToUpdate.length} row(s) updated`;
      showMessage(msg);
      
      // Save to database in background
      try {
        for (const updatedItem of updatedItems) {
          await updateEntry(updatedItem);
        }
      } catch (error) {
        console.error('Fout bij batch edit:', error);
        showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
        // Revert to original on error
        setEntries(prevEntries => 
          prevEntries.map(entry => {
            const original = itemsToUpdate.find(o => o.Id === entry.Id);
            return original || entry;
          })
        );
      }
      
    } else if (dialogMode === 'BatchCopy') {
      // Optimistic update for batch copy
      const itemsToCopy = [...selectedItems];
      const newItems = [];
      
      itemsToCopy.forEach(item => {
        const newItem = { ...item };
        
        // Update fields with new data, keep original if "different values" placeholder
        Object.keys(data).forEach((key) => {
          if (data[key] && data[key] !== '' && data[key] !== '-- verschilt per regel --') {
            newItem[key] = data[key];
          }
        });

        // Generate temporary ID for optimistic update
        const tempId = 'temp_' + Date.now() + '_' + Math.random();
        newItem.Id = tempId;
        delete newItem._firestoreId;
        
        newItems.push(newItem);
      });

      // Add to UI immediately - prepend batch to top when no sort active
      setEntries(prevEntries => [...newItems, ...prevEntries]);
      setDetailsDialogOpen(false);
      const msg = language === 'nl'
        ? `${itemsToCopy.length} regel(s) gekopieerd`
        : `${itemsToCopy.length} row(s) copied`;
      showMessage(msg);
      
      // Save to database in background
      try {
        const savedItems = [];
        for (let i = 0; i < itemsToCopy.length; i++) {
          const itemToCopy = itemsToCopy[i];
          const tempId = newItems[i].Id;
          const copyData = { ...itemToCopy };
          
          // Apply changes from form
          Object.keys(data).forEach((key) => {
            if (data[key] && data[key] !== '' && data[key] !== '-- verschilt per regel --') {
              copyData[key] = data[key];
            }
          });
          
          delete copyData.Id;
          delete copyData._firestoreId;
          
          const savedItem = await insertEntry({ ...copyData, Project: selectedProject });
          savedItems.push({ tempId, savedItem });
        }
        
        // Replace temp entries with real entries
        setEntries(prevEntries => 
          prevEntries.map(entry => {
            const saved = savedItems.find(s => s.tempId === entry.Id);
            return saved ? saved.savedItem : entry;
          })
        );
      } catch (error) {
        console.error('Fout bij batch copy:', error);
        showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
        // Remove temp entries on error
        const tempIds = newItems.map(item => item.Id);
        setEntries(prevEntries => 
          prevEntries.filter(entry => !tempIds.includes(entry.Id))
        );
      }
    }
    
    setSelectedItems([]);
  };

  const handleDeleteEntry = async (entry) => {
    // Remove from UI immediately (optimistic update)
    setEntries(prevEntries => 
      prevEntries.filter(e => e.Id !== entry.Id)
    );
    showMessage(language === 'nl' ? 'Regel verwijderd' : 'Row deleted');
    
    // Delete from database in background
    try {
      await deleteEntry(entry);
    } catch (error) {
      console.error('Fout bij verwijderen:', error);
      showMessage(language === 'nl' ? 'Fout bij verwijderen uit database' : 'Error deleting from database');
      // Re-add on error
      setEntries(prevEntries => [...prevEntries, entry]);
    }
  };

  // Selection handlers
  const handleSelectionChanged = useCallback((event) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedItems(selectedRows);
  }, []);

  // Batch operations
  const handleBatchDelete = async () => {
    if (selectedItems.length === 0) {
      return;
    }

    const itemsToDelete = [...selectedItems];
    const deletedIds = itemsToDelete.map(item => item.Id);
    
    // Remove from UI immediately (optimistic update)
    setEntries(prevEntries => 
      prevEntries.filter(entry => !deletedIds.includes(entry.Id))
    );
    setSelectedItems([]);
    const msg = language === 'nl'
      ? `${itemsToDelete.length} regel(s) verwijderd`
      : `${itemsToDelete.length} row(s) deleted`;
    showMessage(msg);
    
    // Delete from database in background
    try {
      await deleteMultipleEntries(itemsToDelete);
    } catch (error) {
      console.error('Fout bij verwijderen:', error);
      showMessage(language === 'nl' ? 'Fout bij verwijderen uit database' : 'Error deleting from database');
      // Re-add on error
      setEntries(prevEntries => [...prevEntries, ...itemsToDelete]);
    }
  };

  const handleBatchEdit = () => {
    if (selectedItems.length === 0) {
      return;
    }
    setCurrentItem(selectedItems); // Pass all selected items
    setDialogMode('BatchEdit');
    setDetailsDialogOpen(true);
  };

  const handleBatchCopy = () => {
    if (selectedItems.length === 0) {
      return;
    }
    setCurrentItem(selectedItems); // Pass all selected items
    setDialogMode('BatchCopy');
    setDetailsDialogOpen(true);
  };

  // Show links
  const handleShowLinks = () => {
    if (selectedItems.length === 0) {
      return;
    }
    setLinkCoderDialogOpen(true);
  };

  // AI Template Generator
  const handleOpenAI = () => {
    if (!selectedProject) {
      return;
    }
    setAiDialogOpen(true);
  };

  const handleAIApply = async (generatedEntries) => {
    try {
      // Add generated entries to the DataGrid
      const newEntries = [];
      const tempIds = [];

      // First, add all entries optimistically to the UI
      for (let i = 0; i < generatedEntries.length; i++) {
        const tempId = 'temp_ai_' + Date.now() + '_' + i;
        tempIds.push(tempId);
        newEntries.push({ ...generatedEntries[i], Id: tempId });
      }

      // Add to UI immediately (optimistic update)
      setEntries(prevEntries => [...newEntries, ...prevEntries]);
      
      const msg = language === 'nl' 
        ? `${generatedEntries.length} AI-gegenereerde entries toegevoegd` 
        : `${generatedEntries.length} AI-generated entries added`;
      showMessage(msg);
      
      setAiDialogOpen(false);

      // Save to database in background
      try {
        const savedEntries = [];
        for (let i = 0; i < generatedEntries.length; i++) {
          const entry = generatedEntries[i];
          const tempId = tempIds[i];
          
          const savedEntry = await insertEntry({ ...entry, Project: selectedProject });
          savedEntries.push({ tempId, savedEntry });
        }

        // Replace temp entries with real entries
        setEntries(prevEntries => 
          prevEntries.map(entry => {
            const saved = savedEntries.find(s => s.tempId === entry.Id);
            return saved ? saved.savedEntry : entry;
          })
        );
      } catch (error) {
        console.error('Fout bij opslaan AI entries:', error);
        showMessage(language === 'nl' ? 'Fout bij opslaan naar database' : 'Error saving to database');
        
        // Remove temp entries on error
        setEntries(prevEntries => 
          prevEntries.filter(entry => !tempIds.includes(entry.Id))
        );
      }
    } catch (error) {
      console.error('Error applying AI entries:', error);
      const errorMsg = language === 'nl'
        ? 'Fout bij toevoegen van AI entries: ' + error.message
        : 'Error adding AI entries: ' + error.message;
      showMessage(errorMsg);
    }
  };

  // Admin options
  const handleOpenOptions = () => {
    navigate('/options');
  };

  const handleSaveProject = async (project) => {
    try {
      await saveProject(project);
      await loadProjects();
      showMessage(language === 'nl' ? 'Nieuw project opgeslagen' : 'New project saved');
    } catch (error) {
      console.error('Fout bij opslaan project:', error);
      showMessage(language === 'nl' ? 'Fout bij opslaan project' : 'Error saving project');
    }
  };

  const handleDeleteProject = async (project) => {
    try {
      await deleteProject(project);
      await loadProjects();
      showMessage(language === 'nl' ? 'Project verwijderd' : 'Project deleted');
      if (selectedProject === project) {
        setSelectedProject('');
        localStorage.removeItem('linkcoder-selected-project');
      }
    } catch (error) {
      console.error('Fout bij verwijderen project:', error);
      showMessage(language === 'nl' ? 'Fout bij verwijderen project' : 'Error deleting project');
    }
  };


  // Get translations
  const t = getTranslations(language);

  // Als gebruiker niet is ingelogd, toon alleen de basis interface (geen data)
  if (!currentUser) {
    return (
      <div className="wrapperDiv">
        <Header 
          language={language}
          onLanguageChange={handleLanguageChange}
        />
        
        <div className="toolbar">
          <Button
            variant="contained"
            disabled
            className="btn-primary btn-add"
          >
            +
          </Button>

          <IconButton
            disabled
            className="ai-button"
            sx={{
              backgroundColor: 'rgba(0,0,0,0.12)',
              color: 'rgba(0,0,0,0.26)',
              width: 40,
              height: 40,
            }}
          >
            <SmartToyIcon />
          </IconButton>

          <Button
            variant="contained"
            disabled
            className="btn-primary"
          >
            {t.showLinks}
          </Button>

          <div className="select-container">
            <FormControl fullWidth size="small" disabled>
              <InputLabel id="project-select-label">{t.selectProject}</InputLabel>
              <Select
                labelId="project-select-label"
                value=""
                label={t.selectProject}
                className="select-field"
              >
                <MenuItem value="">{t.selectProject}</MenuItem>
              </Select>
            </FormControl>
          </div>

          <Button
            variant="outlined"
            disabled
            className="btn-secondary"
          >
            {t.options}
          </Button>

          <div style={{ flex: 1 }} />

          <div className="batch-buttons-group">
            <Button variant="outlined" disabled className="btn-secondary">
              {t.batchDelete}
            </Button>

            <Button variant="outlined" disabled className="btn-secondary">
              {t.batchEdit}
            </Button>

            <Button variant="outlined" disabled className="btn-secondary">
              {t.batchCopy}
            </Button>
          </div>
        </div>

        <div className="disabled-message">
          {t.loading}
        </div>
      </div>
    );
  }

  return (
    <div className="wrapperDiv">
      <Header 
        language={language}
        onLanguageChange={handleLanguageChange}
      />
      
      <div className="toolbar">
        <Button
          variant="contained"
          onClick={handleAddLink}
          disabled={!selectedProject}
          className="btn-primary btn-add"
        >
          +
        </Button>

        <IconButton
          onClick={handleOpenAI}
          disabled={!selectedProject}
          className="ai-button"
          sx={{
            backgroundColor: !selectedProject ? 'rgba(0,0,0,0.12)' : '#4CAF50',
            color: 'white',
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: !selectedProject ? 'rgba(0,0,0,0.12)' : '#45a049',
            },
            '&:disabled': {
              backgroundColor: 'rgba(0,0,0,0.12)',
              color: 'rgba(0,0,0,0.26)',
            }
          }}
        >
          <SmartToyIcon />
        </IconButton>

        <Button
          variant="contained"
          onClick={handleShowLinks}
          disabled={selectedItems.length === 0}
          className="btn-primary"
        >
          {t.showLinks}
        </Button>

        <div className="select-container">
          <FormControl fullWidth size="small">
            <InputLabel id="project-select-label">{t.selectProject}</InputLabel>
            <Select
              labelId="project-select-label"
              value={selectedProject}
              label={t.selectProject}
              onChange={handleProjectChange}
              className="select-field"
            >
              <MenuItem value="">{t.selectProject}</MenuItem>
              {projects.map((proj) => (
                <MenuItem key={proj} value={proj}>
                  {proj}
                </MenuItem>
              ))}
              <Divider sx={{ my: 1 }} />
              <MenuItem 
                value="__manage_projects__"
                className="manage-projects-menu-item"
              >
                <AddIcon sx={{ fontSize: 18, mr: 1 }} />
                {t.manageProjects}
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        <Button
          variant="outlined"
          onClick={handleOpenOptions}
          className="btn-secondary"
        >
          {t.options}
        </Button>

        <div style={{ flex: 1 }} />

        <div className="batch-buttons-group">
          <Button
            variant="outlined"
            onClick={handleBatchDelete}
            disabled={selectedItems.length === 0}
            className="btn-secondary"
          >
            {t.batchDelete}
          </Button>

          <Button
            variant="outlined"
            onClick={handleBatchEdit}
            disabled={selectedItems.length === 0}
            className="btn-secondary"
          >
            {t.batchEdit}
          </Button>

          <Button
            variant="outlined"
            onClick={handleBatchCopy}
            disabled={selectedItems.length === 0}
            className="btn-secondary"
          >
            {t.batchCopy}
          </Button>
        </div>
      </div>

      <div className="data-grid-container">
        {loading ? (
          <div className="loading-container">
            {t.loading}
          </div>
        ) : (
          <DataGrid
            rowData={entries}
            onSelectionChanged={handleSelectionChanged}
            onRowDoubleClick={handleRowDoubleClick}
            onEdit={handleEditEntry}
            onClone={handleCloneEntry}
            onDelete={handleDeleteEntry}
            onCellValueChanged={handleCellValueChanged}
            fieldConfig={fieldConfig}
            language={language}
          />
        )}
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>

      <DetailsDialog
        isOpen={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        onSave={handleSaveEntry}
        item={currentItem}
        mode={dialogMode}
        fieldConfig={fieldConfig}
        projectId={selectedProject}
        language={language}
      />

      <LinkCoderDialog
        isOpen={linkCoderDialogOpen}
        onClose={() => setLinkCoderDialogOpen(false)}
        selectedItems={selectedItems}
        linkTemplate={fieldConfig?.linkTemplate}
        language={language}
      />

      <ManageProjectsModal
        isOpen={manageProjectsModalOpen}
        onClose={() => setManageProjectsModalOpen(false)}
        projects={projects}
        onSaveProject={handleSaveProject}
        onDeleteProject={handleDeleteProject}
        language={language}
      />

      <AILinkCoderDialog
        isOpen={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        onApply={handleAIApply}
        fieldConfig={fieldConfig}
        language={language}
      />
    </div>
  );
};

export default MainPage;

